import { ReactNode } from "react";
import { Navigate } from "react-router";
import { useAuth } from "./hooks";
import { paths } from "@shared-core/routing/routes";

type GuardRole =
  | "admin"
  | "manager"
  | "sales_rep"
  | "csr"
  | "engineer"
  | "commercial"
  | "finance"
  | "marketing"
  | "stock_control";

interface ProtectedRouteProps {
  children: ReactNode;
  /** A single role or any-of list. Defaults to admin-only. */
  requiredRole?: GuardRole | GuardRole[];
}

/**
 * Component to protect routes based on user role.
 * If user doesn't have one of the required roles, redirects to dashboard home.
 */
export function ProtectedRoute({
  children,
  requiredRole = "admin",
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();

  // Show nothing while loading (or could show a loading spinner)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const allowed = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

  // No user or wrong role - redirect to dashboard home
  if (!user || !allowed.includes(user.role as GuardRole)) {
    return <Navigate to={paths.root()} replace />;
  }

  return <>{children}</>;
}
